/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business レーティング分析の指標定義（ビジネス定義・技術定義）。KpiDefinitionView と同じパターンで business/technical を 1:1 で表示する。
 * @graph-connects none
 */
export interface RatingMetricDefinition {
  label: string;
  businessDescription: string;
  technicalSource: {
    database: string;
    tables: string[];
    logic: string;
  };
}

/** @graph-connects none */
export type RatingMetricKey =
  | 'totalOrders'
  | 'avgRating'
  | 'avgDesignRating'
  | 'avgColorRating'
  | 'goodRate'
  | 'ratingNullRate'
  | 'stylingReviewNullRate';

/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business レーティング分析7指標のビジネス定義・技術定義を保持する
 * @graph-connects none
 */
export const RATING_DEFINITIONS: Record<RatingMetricKey, RatingMetricDefinition> = {
  totalOrders: {
    label: 'オーダー数',
    businessDescription:
      '期間内にスタイリング完了（選定日起算）または返却受領（返却日起算）されたオーダーの総数。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: [
        'mart__styling_histories',
        'mart__orders',
        'database_no_embedding.MENS_MONGO_PROD_orders',
      ],
      logic:
        "mart__styling_histories WHERE status='STYLING_COMPLETED' を mart__orders と JOIN。選定日起算: completed_at、返却日起算: database_no_embedding.MENS_MONGO_PROD_orders.received_at で期間フィルタ。",
    },
  },
  avgRating: {
    label: '平均レーティング',
    businessDescription:
      'デザイン評価とカラー評価の平均値（1〜3スケール）。着用アイテム（is_used=yes）のみ対象。アイテムごとの design_rating と color_rating の平均を算出し、さらにオーダー平均を取る。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: ['database_no_embedding.MENS_MONGO_PROD_orders'],
      logic:
        "AVG((design_rating + color_rating) / 2) from order_review.item_review[] WHERE is_used='yes'。オーダーごとの着用アイテム平均を算出後、日次集約。",
    },
  },
  avgDesignRating: {
    label: 'デザイン評価平均',
    businessDescription:
      'デザイン評価の平均値（1〜3スケール）。着用アイテムのみ対象。1=低評価、3=高評価。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: ['database_no_embedding.MENS_MONGO_PROD_orders'],
      logic: "AVG(design_rating) from order_review.item_review[] WHERE is_used='yes'",
    },
  },
  avgColorRating: {
    label: 'カラー評価平均',
    businessDescription:
      'カラー評価の平均値（1〜3スケール）。着用アイテムのみ対象。1=低評価、3=高評価。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: ['database_no_embedding.MENS_MONGO_PROD_orders'],
      logic: "AVG(color_rating) from order_review.item_review[] WHERE is_used='yes'",
    },
  },
  goodRate: {
    label: 'スタイリストGood率',
    businessDescription:
      'スタイリスト評価でGOODを選択した割合（%）。GOOD/(GOOD+BAD)×100。未回答は分母に含まない。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: ['database_no_embedding.MENS_MONGO_PROD_orders'],
      logic:
        "COUNTIF(styling_review='1') / NULLIF(COUNTIF(styling_review IS NOT NULL), 0) × 100。styling_review: 1=GOOD, 0=BAD, NULL=未回答。",
    },
  },
  ratingNullRate: {
    label: 'レーティング未回答率',
    businessDescription:
      'アイテム評価（design_rating/color_rating）が未回答のオーダーの割合。item_review が存在しないか、全アイテムの評価がNULLのオーダーを未回答とする。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: ['database_no_embedding.MENS_MONGO_PROD_orders'],
      logic: '(totalOrders - ratingRespondedOrders) / totalOrders × 100',
    },
  },
  stylingReviewNullRate: {
    label: 'スタイリスト評価未回答率',
    businessDescription:
      'スタイリスト評価（GOOD/BAD選択）が未回答のオーダーの割合。styling_review が NULL のオーダーを未回答とする。',
    technicalSource: {
      database: 'BigQuery (ac_mens_hub)',
      tables: ['database_no_embedding.MENS_MONGO_PROD_orders'],
      logic: '(totalOrders - stylingReviewRespondedOrders) / totalOrders × 100',
    },
  },
};
